import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './MultiVariantNetwork.css';

const { forceCollide, forceCenter } = require('d3-force');

/* ------------------------------- CONSTANTS ------------------------------- */

const BASE_NODE_COLORS = {
  variant: '#3b82f6',
  gene: '#8b5cf6',
  drug: '#f97316',
  protein: '#10b981',
  phenotype: '#ef4444',
  unknown: '#94a3b8'
};

const NODE_COLOR_POOL = [
  '#22d3ee',
  '#facc15',
  '#6366f1',
  '#14b8a6',
  '#f472b6',
  '#fb7185',
  '#a3e635',
  '#0ea5e9',
  '#f97316',
  '#38bdf8'
];

const INTERACTION_COLOR_POOL = [
  '#fb7185',
  '#f97316',
  '#38bdf8',
  '#10b981',
  '#a855f7',
  '#fcd34d',
  '#34d399',
  '#c084fc'
];

/* -------------------------------- HELPERS -------------------------------- */

const getFirstValue = (source = {}, candidates = []) => {
  for (const key of candidates) {
    if (
      key in source &&
      source[key] !== undefined &&
      source[key] !== null &&
      String(source[key]).trim() !== ''
    ) {
      return source[key];
    }
  }
  return '';
};

const normalizeType = (value) => {
  if (value === null || value === undefined) return 'unknown';
  const asString = String(value).trim();
  if (!asString) return 'unknown';
  return asString.toLowerCase();
};

const formatTypeLabel = (value) => {
  const type = (value || 'unknown').toString();
  return type
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const extractNumericWeight = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const numeric = parseFloat(value);
  if (Number.isFinite(numeric)) return numeric;
  const match = String(value).match(/-?\d+(\.\d+)?(e-?\d+)?/i);
  return match ? parseFloat(match[0]) : NaN;
};

const formatRangeValue = (value) => {
  if (!Number.isFinite(value)) return 'n/a';
  const abs = Math.abs(value);
  if (abs >= 1) return value.toFixed(2);
  if (abs >= 0.01) return value.toFixed(3);
  return value.toExponential(2);
};

/* ------------------------------ PERF HOOKS ------------------------------- */

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function useRafThrottledSetter(setter) {
  const rafId = useRef(null);
  const latest = useRef();

  const throttled = useCallback((val) => {
    latest.current = val;
    if (rafId.current !== null) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      setter(latest.current);
    });
  }, [setter]);

  useEffect(() => {
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return throttled;
}

function useResizeObserver(ref, onSize) {
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const cr = entry.contentRect;
      onSize({ width: cr.width, height: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, onSize]);
}

/* --------------------------- TABLE (VIRTUALIZED) -------------------------- */

const ROW_HEIGHT = 34;
const OVERSCAN = 8;

const NetworkTableModal = ({
  rows, // [{id, source, target, interactionType, weight}]
  tableDefaultVisible,
  tableOverridesMap,
  setTableDefaultVisible,
  setTableOverridesMap,
  onClose
}) => {
  const [search, setSearch] = useState('');
  const tableBodyRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(300);
  const [scrollTop, setScrollTop] = useState(0);

  // track viewport size
  useEffect(() => {
    const el = tableBodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      setViewportHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = tableBodyRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const searchDebounced = useDebouncedValue(search, 120);

  const filteredRowIds = useMemo(() => {
    if (!searchDebounced.trim()) {
      return rows.map((r) => r.id);
    }
    const q = searchDebounced.toLowerCase();
    const out = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (
        r.source.toLowerCase().includes(q) ||
        r.target.toLowerCase().includes(q) ||
        String(r.interactionType || '').toLowerCase().includes(q)
      ) {
        out.push(r.id);
      }
    }
    return out;
  }, [rows, searchDebounced]);

  // virtualization window
  const total = filteredRowIds.length;
  const startIdx = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN
  );
  const endIdx = Math.min(
    total,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN
  );
  const sliceIds = filteredRowIds.slice(startIdx, endIdx);
  const topSpacer = startIdx * ROW_HEIGHT;
  const bottomSpacer = (total - endIdx) * ROW_HEIGHT;

  const isRowVisible = useCallback(
    (id) => {
      if (tableOverridesMap.has(id)) return tableOverridesMap.get(id);
      return tableDefaultVisible;
    },
    [tableDefaultVisible, tableOverridesMap]
  );

  const visibleCount = useMemo(() => {
    let count = tableDefaultVisible ? rows.length : 0;
    tableOverridesMap.forEach((v) => {
      if (tableDefaultVisible) count += v ? 0 : -1;
      else count += v ? 1 : 0;
    });
    return count;
  }, [rows.length, tableDefaultVisible, tableOverridesMap]);

  const toggleRow = useCallback(
    (id, nextVisible) => {
      setTableOverridesMap((prev) => {
        const next = new Map(prev);
        const resolvedDefault = tableDefaultVisible;
        const currentResolved = next.has(id) ? next.get(id) : resolvedDefault;
        if (currentResolved === nextVisible) return prev;
        if (nextVisible === resolvedDefault) next.delete(id);
        else next.set(id, nextVisible);
        return next;
      });
    },
    [setTableOverridesMap, tableDefaultVisible]
  );

  const toggleAll = useCallback(
    (value) => {
      setTableDefaultVisible(value);
      setTableOverridesMap(() => new Map());
    },
    [setTableDefaultVisible, setTableOverridesMap]
  );

  return (
    <div className="network-table-modal">
      <div className="network-table-modal-header">
        <h3>Network Data</h3>
        <button className="network-table-modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="network-table-modal-controls">
        <div className="network-table-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="network-table-actions">
          <span className="network-table-stats">{visibleCount} / {rows.length}</span>
          <button onClick={() => toggleAll(true)} className="network-table-action-btn">All</button>
          <button onClick={() => toggleAll(false)} className="network-table-action-btn">None</button>
        </div>
      </div>

      <div className="network-table-modal-body" ref={tableBodyRef}>
        <table className="network-data-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={visibleCount === rows.length && rows.length > 0}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </th>
              <th>Source</th>
              <th>Target</th>
              <th>Type</th>
              <th>Weight</th>
            </tr>
          </thead>
          <tbody>
            {/* top spacer */}
            <tr>
              <td colSpan={5} style={{ height: topSpacer }} />
            </tr>

            {sliceIds.map((id) => {
              const r = rows[id];
              const checked = isRowVisible(id);
              return (
                <tr key={id} style={{ height: ROW_HEIGHT }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleRow(id, e.target.checked)}
                    />
                  </td>
                  <td>{r.source}</td>
                  <td>{r.target}</td>
                  <td>{r.interactionType}</td>
                  <td>{Number.isFinite(r.weight) ? r.weight.toFixed(3) : 'N/A'}</td>
                </tr>
              );
            })}

            {/* bottom spacer */}
            <tr>
              <td colSpan={5} style={{ height: bottomSpacer }} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------------------- MAIN COMPONENT ----------------------------- */

const MultiVariantNetwork = ({ data }) => {
  const containerRef = useRef(null);
  const fgRef = useRef(null);
  const [isLightTheme, setIsLightTheme] = useState(() => {
    if (typeof document === 'undefined') return false;
    const root = document.querySelector('.WSNG-app-container');
    return root ? root.classList.contains('light-theme') : false;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.querySelector('.WSNG-app-container');
    if (!root || typeof MutationObserver === 'undefined') {
      return undefined;
    }

    const updateTheme = () => {
      setIsLightTheme(root.classList.contains('light-theme'));
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // color caches
  const nodeColorCache = useRef({ ...BASE_NODE_COLORS });
  const nodePaletteIndexRef = useRef(0);
  const interactionColorCache = useRef({});
  const interactionPaletteIndexRef = useRef(0);

  const ensureNodeColor = useCallback((type) => {
    const key = normalizeType(type);
    if (!nodeColorCache.current[key]) {
      const colour =
        NODE_COLOR_POOL[nodePaletteIndexRef.current % NODE_COLOR_POOL.length] ||
        '#38bdf8';
      nodeColorCache.current[key] = colour;
      nodePaletteIndexRef.current += 1;
    }
    return nodeColorCache.current[key];
  }, []);

  const ensureInteractionColor = useCallback((type) => {
    const key = (type || 'unknown').toString();
    if (!interactionColorCache.current[key]) {
      const colour =
        INTERACTION_COLOR_POOL[
          interactionPaletteIndexRef.current % INTERACTION_COLOR_POOL.length
        ] || '#f59342';
      interactionColorCache.current[key] = colour;
      interactionPaletteIndexRef.current += 1;
    }
    return interactionColorCache.current[key];
  }, []);

  /* ---------------------------- Parse input once --------------------------- */

  const parsed = useMemo(() => {
    const empty = {
      nodes: [],
      links: [],            // immutable: never given to ForceGraph directly
      idToNode: new Map(),
      nameLower: new Map(),
      adjacency: new Map(),
      nodeTypeCounts: {},
      interactionCounts: {},
      interactionNodeTypes: {},
      weights: [0, 1],
      tableRows: [],
      nodeTypes: [],
      interactionTypes: []
    };

    if (!data || typeof data !== 'string') return empty;

    const lines = data.trim().split('\n');
    if (lines.length < 2) return empty;

    const headers = lines[0].split('\t').map((h) => h.trim());
    const nodesMap = new Map(); // id -> node
    const links = [];           // store stable srcId/tgtId separately

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      if (!values.length) continue;

      const row = {};
      for (let j = 0; j < headers.length; j++) {
        row[headers[j]] = values[j] !== undefined ? values[j].trim() : '';
      }

      const source = getFirstValue(row, [
        'from',
        'source',
        'node1',
        'node_1',
        'source_id'
      ]);
      const target = getFirstValue(row, [
        'to',
        'target',
        'node2',
        'node_2',
        'target_id'
      ]);
      if (!source || !target) continue;

      const sourceTypeRaw = getFirstValue(row, [
        'from.type',
        'from_type',
        'fromtype',
        'source_type',
        'sourceType',
        'from_class'
      ]);
      const targetTypeRaw = getFirstValue(row, [
        'to.type',
        'to_type',
        'totype',
        'target_type',
        'targetType',
        'to_class'
      ]);
      const interactionTypeRaw = getFirstValue(row, [
        'Type',
        'type',
        'interaction_type',
        'interaction',
        'relationship'
      ]);
      const weightRaw = getFirstValue(row, [
        'Weight',
        'weight',
        'score',
        'Score',
        'confidence',
        'Confidence',
        'support',
        'Support',
        'posterior',
        'Posterior',
        'pvalue',
        'p_value',
        'probability'
      ]);

      const sourceType = normalizeType(sourceTypeRaw);
      const targetType = normalizeType(targetTypeRaw);
      const interactionType = interactionTypeRaw || 'unknown';
      const weight = extractNumericWeight(weightRaw);

      if (!nodesMap.has(source)) {
        nodesMap.set(source, { id: source, name: source, type: sourceType });
      }
      if (!nodesMap.has(target)) {
        nodesMap.set(target, { id: target, name: target, type: targetType });
      }

      links.push({
        id: `${source}-${interactionType}-${target}-${i}`,
        srcId: source,       // STABLE string ids (never mutated)
        tgtId: target,
        sourceType,
        targetType,
        interactionType,
        weight: Number.isFinite(weight) ? weight : 0
      });
    }

    const nodes = Array.from(nodesMap.values());

    // Precompute helpers
    const idToNode = new Map();
    const nameLower = new Map();
    const adjacency = new Map(); // id -> Set(neighbors)
    for (const n of nodes) {
      idToNode.set(n.id, n);
      nameLower.set(n.id, (n.name || '').toString().toLowerCase());
      adjacency.set(n.id, new Set());
    }
    for (const l of links) {
      adjacency.get(l.srcId)?.add(l.tgtId);
      adjacency.get(l.tgtId)?.add(l.srcId);
    }

    // Counts & lists
    const nodeTypeCounts = {};
    for (const n of nodes) {
      const k = n.type || 'unknown';
      nodeTypeCounts[k] = (nodeTypeCounts[k] || 0) + 1;
    }

    const interactionCounts = {};
    const interactionNodeTypes = {};
    const interactionTypeSet = new Set();
    for (const l of links) {
      const key = l.interactionType || 'unknown';
      interactionCounts[key] = (interactionCounts[key] || 0) + 1;
      interactionTypeSet.add(key);
      if (!interactionNodeTypes[key]) interactionNodeTypes[key] = new Set();
      if (l.sourceType) interactionNodeTypes[key].add(l.sourceType);
      if (l.targetType) interactionNodeTypes[key].add(l.targetType);
    }

    const weightsArr = links
      .map((l) => l.weight)
      .filter((w) => Number.isFinite(w));
    let minW = 0;
    let maxW = 1;
    if (weightsArr.length) {
      minW = Math.min(...weightsArr);
      maxW = Math.max(...weightsArr);
      if (minW === maxW) {
        if (minW === 0) maxW = 1;
        else {
          const pad = Math.abs(minW) * 0.1 || 0.1;
          minW -= pad;
          maxW += pad;
        }
      }
    }
    if (!Number.isFinite(minW) || !Number.isFinite(maxW)) {
      minW = 0;
      maxW = 1;
    }

    // Table rows: index-aligned for O(1) lookups by id/index
    const tableRows = links.map((l, idx) => ({
      id: idx, // stable index
      source: l.srcId,
      target: l.tgtId,
      interactionType: l.interactionType,
      weight: l.weight
    }));

    // Color bootstrap to avoid first-render hiccups
    const tmpNodeColorCache = new Set();
    for (const n of nodes) {
      const c = BASE_NODE_COLORS[n.type];
      if (!c && !tmpNodeColorCache.has(n.type)) {
        tmpNodeColorCache.add(n.type);
      }
    }

    const nodeTypes = Array.from(new Set(nodes.map((n) => n.type || 'unknown'))).sort();
    const interactionTypes = Array.from(interactionTypeSet).sort();

    return {
      nodes,
      links, // IMPORTANT: these never go to ForceGraph directly
      idToNode,
      nameLower,
      adjacency,
      nodeTypeCounts,
      interactionCounts,
      interactionNodeTypes,
      weights: [minW, maxW],
      tableRows,
      nodeTypes,
      interactionTypes
    };
  }, [data]);

  /* --------------------- State derived from parsed content --------------------- */

  const [placeholder, setPlaceholder] = useState('Loading...');
  useEffect(() => {
    if (!parsed.nodes.length || !parsed.links.length) {
      setPlaceholder('No valid network connections found in the data.');
    } else {
      setPlaceholder(null);
    }
  }, [parsed.nodes.length, parsed.links.length]);

  // Node & interaction visibility
  const [visibleNodeTypes, setVisibleNodeTypes] = useState({});
  const [visibleInteractionTypes, setVisibleInteractionTypes] = useState({});

  useEffect(() => {
    const nodeVis = {};
    parsed.nodeTypes.forEach((t) => (nodeVis[t] = true));

    const interactionVis = {};
    const allowedDefault = new Set([
      'variant-gene-original',
      'variant-phenotype-gwas',
      'variant-phenotype-original'
    ]);
    let hasAny = false;
    for (const t of parsed.interactionTypes) {
      const allow = allowedDefault.has((t || '').toLowerCase());
      interactionVis[t] = allow;
      if (allow) hasAny = true;
    }
    if (!hasAny) {
      for (const t of parsed.interactionTypes) interactionVis[t] = true;
    }

    setVisibleNodeTypes(nodeVis);
    setVisibleInteractionTypes(interactionVis);
  }, [parsed.nodeTypes, parsed.interactionTypes]);

  // Weight range (throttled updates)
  const [scoreBounds, setScoreBounds] = useState([0, 1]);
  const [scoreRange, setScoreRange] = useState([0, 1]);
  useEffect(() => {
    setScoreBounds(parsed.weights);
    setScoreRange(parsed.weights);
  }, [parsed.weights]);

  const throttledSetScoreRange = useRafThrottledSetter(setScoreRange);

  // Search (debounced)
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryDebounced = useDebouncedValue(searchQuery, 140);

  // Table visibility model: default + overrides (Map)
  const [tableDefaultVisible, setTableDefaultVisible] = useState(true);
  const [tableOverridesMap, setTableOverridesMap] = useState(() => new Map());
  useEffect(() => {
    setTableDefaultVisible(true);
    setTableOverridesMap(new Map());
  }, [data]);

  // Dimensions
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useResizeObserver(containerRef, setDimensions);

  /* --------------------------------- Tooltip -------------------------------- */

  const tooltipRef = useRef(null);
  const hoveredLinkRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    const tip = tooltipRef.current;
    if (!el || !tip) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left + 12;
      const y = e.clientY - rect.top - 12;
      tip.style.transform = `translate(${x}px, ${y}px)`;
    };
    el.addEventListener('mousemove', onMove, { passive: true });
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  const hideTooltip = useCallback(() => {
    const tip = tooltipRef.current;
    if (tip) tip.style.display = 'none';
    hoveredLinkRef.current = null;
  }, []);

  const showTooltipForLink = useCallback((link) => {
    if (!link) {
      hideTooltip();
      return;
    }
    if (hoveredLinkRef.current === link) return;
    hoveredLinkRef.current = link;
    const tip = tooltipRef.current;
    if (!tip) return;

    const sId = typeof link.source === 'object' ? link.source.id : link.source;
    const tId = typeof link.target === 'object' ? link.target.id : link.target;
    const sNode = parsed.idToNode.get(sId);
    const tNode = parsed.idToNode.get(tId);
    const sType = formatTypeLabel(sNode?.type || 'unknown');
    const tType = formatTypeLabel(tNode?.type || 'unknown');

    const lines = [
      `${sType} ${sNode?.name || sId} \u2192 ${tType} ${tNode?.name || tId}`,
      `Interaction: ${formatTypeLabel(link.interactionType)}`,
      `Weight: ${formatRangeValue(link.weight)}`
    ];
    tip.textContent = lines.join('\n');
    tip.style.whiteSpace = 'pre-line';
    tip.style.display = 'block';
  }, [hideTooltip, parsed.idToNode]);

  /* ------------------------------- Filtering -------------------------------- */

  const allowedNodeTypesSet = useMemo(() => {
    const s = new Set();
    Object.entries(visibleNodeTypes).forEach(([t, on]) => {
      if (on) s.add(t);
    });
    return s;
  }, [visibleNodeTypes]);

  const allowedInteractionsSet = useMemo(() => {
    const s = new Set();
    Object.entries(visibleInteractionTypes).forEach(([t, on]) => {
      if (on) s.add(t);
    });
    return s;
  }, [visibleInteractionTypes]);

  const searchNodeSets = useMemo(() => {
    const q = (searchQueryDebounced || '').trim().toLowerCase();
    if (!q) {
      return {
        searchActive: false,
        matchingIds: new Set(),
        expandedIds: new Set()
      };
    }
    const matching = new Set();
    parsed.idToNode.forEach((n, id) => {
      if (parsed.nameLower.get(id)?.includes(q)) matching.add(id);
    });

    // if no matches, DON'T filter by search (keeps graph visible)
    if (matching.size === 0) {
      return {
        searchActive: false,
        matchingIds: new Set(),
        expandedIds: new Set()
      };
    }

    const expanded = new Set(matching);
    matching.forEach((id) => {
      const nbrs = parsed.adjacency.get(id);
      if (nbrs) for (const nb of nbrs) expanded.add(nb);
    });
    return { searchActive: true, matchingIds: matching, expandedIds: expanded };
  }, [parsed.idToNode, parsed.nameLower, parsed.adjacency, searchQueryDebounced]);

  const isLinkIndexVisible = useCallback(
    (idx) => (tableOverridesMap.has(idx) ? tableOverridesMap.get(idx) : tableDefaultVisible),
    [tableDefaultVisible, tableOverridesMap]
  );

  // Build filtered graph (uses ONLY stable ids; render clones are created)
  const filtered = useMemo(() => {
    const minScore = scoreRange[0];
    const maxScore = scoreRange[1];

    const nodesSet = new Set();
    const linksRender = [];
    const perInteractionCounts = {};

    for (let i = 0; i < parsed.links.length; i++) {
      const L = parsed.links[i];

      if (!isLinkIndexVisible(i)) continue;
      if (!allowedInteractionsSet.has(L.interactionType)) continue;

      const w = Number.isFinite(L.weight) ? L.weight : 0;
      if (w < minScore || w > maxScore) continue;

      const sNode = parsed.idToNode.get(L.srcId);
      const tNode = parsed.idToNode.get(L.tgtId);
      if (!sNode || !tNode) continue;
      if (!allowedNodeTypesSet.has(sNode.type) || !allowedNodeTypesSet.has(tNode.type)) {
        continue;
      }

if (searchNodeSets.searchActive) {
        // CORRECTED LOGIC:
        // To show only the relevant "islands", we must ensure that BOTH nodes of a link
        // are present in the expanded set (the search matches + their direct neighbors).
        // If either node is outside this set, we discard the link.
        if (
          !searchNodeSets.expandedIds.has(sNode.id) ||
          !searchNodeSets.expandedIds.has(tNode.id)
        ) {
          continue;
        }
      }

      // Collect counts
      const k = L.interactionType || 'unknown';
      perInteractionCounts[k] = (perInteractionCounts[k] || 0) + 1;

      // Collect endpoints
      nodesSet.add(sNode.id);
      nodesSet.add(tNode.id);

      // IMPORTANT: push a FRESH link object for the renderer.
      linksRender.push({
        id: L.id,
        source: L.srcId,   // strings; ForceGraph will resolve once it sees nodes
        target: L.tgtId,
        interactionType: L.interactionType,
        weight: L.weight
      });
    }

    // Fresh node objects for the renderer (avoid FG mutating our parsed copy)
    const nodesRender = [];
    for (const id of nodesSet) {
      nodesRender.push({
        id,
        name: parsed.idToNode.get(id).name,
        type: parsed.idToNode.get(id).type,
        isSearchMatch: searchNodeSets.matchingIds.has(id)
      });
    }

    return {
      nodes: nodesRender,
      links: linksRender,
      perInteractionCounts
    };
  }, [
    parsed.links,
    parsed.idToNode,
    allowedInteractionsSet,
    allowedNodeTypesSet,
    scoreRange,
    isLinkIndexVisible,
    searchNodeSets
  ]);

  /* --------------------------- ForceGraph settings -------------------------- */

  // Always draw labels (per your request)
  const drawNode = useCallback(
    (node, ctx) => {
      const nodeSize = 8;
      const color = BASE_NODE_COLORS[node.type] || nodeColorCache.current[node.type] || '#38bdf8';
      if (!nodeColorCache.current[node.type]) {
        // fall back to deterministic palette if not present
        nodeColorCache.current[node.type] =
          NODE_COLOR_POOL[nodePaletteIndexRef.current % NODE_COLOR_POOL.length] || '#38bdf8';
        nodePaletteIndexRef.current += 1;
      }

      ctx.save();
      ctx.globalAlpha = 0.9;
      ctx.shadowColor = color;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.75;
      ctx.stroke();

      // label (always)
      const labelColor = isLightTheme ? '#101828' : '#ffffff';
      const labelShadow = isLightTheme ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.6)';

      ctx.fillStyle = labelColor;
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.shadowColor = labelShadow;
      ctx.fillText(node.name, node.x, node.y + nodeSize + 4);

      ctx.restore();
    },
    [isLightTheme]
  );

  const drawLink = useCallback(
    (link, ctx) => {
      const sourceNode = typeof link.source === 'object' ? link.source : null;
      const targetNode = typeof link.target === 'object' ? link.target : null;
      if (!sourceNode || !targetNode) return;

      const [min, max] = scoreBounds;
      let normalized = 0.5;
      if (Number.isFinite(min) && Number.isFinite(max) && max !== min) {
        const clamped = Math.max(min, Math.min(link.weight, max));
        normalized = (clamped - min) / (max - min);
      }

      const c =
        INTERACTION_COLOR_POOL[
          interactionPaletteIndexRef.current % INTERACTION_COLOR_POOL.length
        ] || '#f59342';
      // cache per type
      if (!interactionColorCache.current[link.interactionType]) {
        interactionColorCache.current[link.interactionType] = c;
        interactionPaletteIndexRef.current += 1;
      }
      const color = interactionColorCache.current[link.interactionType];

      const alpha = 0.4 + normalized * 0.35;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1 + normalized * 2;
      ctx.beginPath();
      ctx.moveTo(sourceNode.x, sourceNode.y);
      ctx.lineTo(targetNode.x, targetNode.y);
      ctx.stroke();
      ctx.restore();
    },
    [scoreBounds]
  );

  const nodeLabel = useCallback(
    (node) => `${formatTypeLabel(node.type)}: ${node.name}`,
    []
  );

  // Configure forces once and refit on first data load only
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!fgRef.current) return;

    fgRef.current.d3Force('charge')?.strength(-10);
    fgRef.current.d3Force('center', forceCenter());
    if (parsed.nodes.length <= 1200) {
      fgRef.current.d3Force('collision', forceCollide().radius(28));
    } else {
      fgRef.current.d3Force('collision', null);
    }
  }, [parsed.nodes.length]);

  useEffect(() => {
    if (!fgRef.current) return;
    if (!didInitialFitRef.current && filtered.nodes.length > 0) {
      didInitialFitRef.current = true;
      setTimeout(() => fgRef.current?.zoomToFit(400, 50), 200);
    }
  }, [filtered.nodes.length]);

  /* --------------------------------- UI bits -------------------------------- */

  const handleNodeTypeChange = useCallback((type) => {
    setVisibleNodeTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const handleInteractionTypeChange = useCallback((type) => {
    setVisibleInteractionTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const [showTableModal, setShowTableModal] = useState(false);

  // Slider config (throttled on-change to avoid floods)
  const sliderSpan = scoreBounds[1] - scoreBounds[0];
  const sliderStep = sliderSpan > 0 ? Math.max(sliderSpan / 100, 0.0001) : 0.01;
  const sliderDisabled = !parsed.links.length || sliderSpan <= 0;

  /* ---------------------------------- RENDER --------------------------------- */

  if (!data) {
    return (
      <div className="multivariant-network-container">
        <div className="network-placeholder">No network data available</div>
      </div>
    );
  }

  return (
    <div className="multivariant-network-container">
      <div className="multivariant-network-controls">
        <div className="score-range-control">
          <label>Weight Filter:</label>
          <Slider
            range
            min={scoreBounds[0]}
            max={scoreBounds[1]}
            step={sliderStep}
            allowCross={false}
            value={[
              Math.max(scoreBounds[0], Math.min(scoreRange[0], scoreBounds[1])),
              Math.max(scoreBounds[0], Math.min(scoreRange[1], scoreBounds[1]))
            ]}
            disabled={sliderDisabled}
            onChange={(val) => throttledSetScoreRange(val)}
            onAfterChange={(val) => setScoreRange(val)}
            className="weight-slider"
          />
          <span className="range-display">
            {`${formatRangeValue(scoreRange[0])} - ${formatRangeValue(scoreRange[1])}`}
          </span>
        </div>

        {/* Raw file stats */}
        <div className="data-breakdown">
          <div className="breakdown-section">
            <h4>Raw File Contents</h4>
            <div className="breakdown-stats">
              <div className="stat-group">
                <span className="stat-title">Node Types:</span>
                {Object.entries(parsed.nodeTypeCounts).map(([type, count]) => (
                  <span key={type} className="stat-item">
                    {formatTypeLabel(type)}: {count}
                  </span>
                ))}
              </div>
              <div className="stat-group">
                <span className="stat-title">Interaction Types:</span>
                {Object.entries(parsed.interactionCounts).map(([type, count]) => (
                  <span key={type} className="stat-item">
                    {formatTypeLabel(type)}: {count}
                  </span>
                ))}
              </div>
              <div className="stat-group">
                <span className="stat-title">Totals:</span>
                <span className="stat-item">Nodes: {parsed.nodes.length}</span>
                <span className="stat-item">Edges: {parsed.links.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-controls">
          <div className="filter-group">
            <label>Node Types:</label>
            <div className="checkbox-group">
              {parsed.nodeTypes.map((type) => {
                const isChecked = Object.prototype.hasOwnProperty.call(
                  visibleNodeTypes,
                  type
                )
                  ? visibleNodeTypes[type]
                  : true;
                const total = parsed.nodeTypeCounts[type] || 0;
                return (
                  <label
                    key={type}
                    className="checkbox-item"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.4)',
                        backgroundColor: BASE_NODE_COLORS[type] || '#38bdf8'
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleNodeTypeChange(type)}
                      style={{ marginRight: 6 }}
                    />
                    <span className="checkbox-label">{formatTypeLabel(type)}s</span>
                    <span
                      className="checkbox-count"
                      style={{
                        marginLeft: 'auto',
                        fontVariantNumeric: 'tabular-nums',
                        opacity: 0.8
                      }}
                    >
                      {total}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="filter-group">
            <label>Interactions:</label>
            <div className="checkbox-group">
              {parsed.interactionTypes.map((type) => {
                const isChecked = Object.prototype.hasOwnProperty.call(
                  visibleInteractionTypes,
                  type
                )
                  ? visibleInteractionTypes[type]
                  : true;
                const total = parsed.interactionCounts[type] || 0;
                const typeOrbs = Array.from(parsed.interactionNodeTypes[type] || []).sort();
                return (
                  <label
                    key={type}
                    className="checkbox-item"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {typeOrbs.map((nodeType) => (
                        <span
                          key={nodeType}
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.4)',
                            backgroundColor: BASE_NODE_COLORS[nodeType] || '#38bdf8'
                          }}
                        />
                      ))}
                    </span>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleInteractionTypeChange(type)}
                      style={{ marginRight: 6 }}
                    />
                    <span className="checkbox-label">{formatTypeLabel(type)}</span>
                    <span
                      className="checkbox-count"
                      style={{
                        marginLeft: 'auto',
                        fontVariantNumeric: 'tabular-nums',
                        opacity: 0.8
                      }}
                    >
                      {total}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="network-metrics">
          <div className="metric-item">
            <span className="metric-value">{filtered.nodes.length}</span>
            <span className="metric-label">Filtered Nodes</span>
          </div>
          <div className="metric-item">
            <span className="metric-value">{filtered.links.length}</span>
            <span className="metric-label">Filtered Edges</span>
          </div>
          <div className="metric-item">
            <span className="metric-value">
              {Object.entries(filtered.perInteractionCounts)
                .map(([type, count]) => `${formatTypeLabel(type)} (${count})`)
                .join(', ') || 'All'}
            </span>
            <span className="metric-label">Active Interactions</span>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="multivariant-network-graph">
        {/* search box */}
        <div className="network-search-container">
          <div className="network-search-bar">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search node labels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="network-search-input"
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {searchQueryDebounced && (
            <div className="network-search-results">
              {(() => {
                const matchCount = filtered.nodes.reduce(
                  (acc, n) => acc + (n.isSearchMatch ? 1 : 0),
                  0
                );
                const totalVisible = filtered.nodes.length;
                return matchCount > 0
                  ? `Found ${matchCount} matching node${matchCount !== 1 ? 's' : ''} (showing ${totalVisible} total with connections)`
                  : 'No matching nodes found';
              })()}
            </div>
          )}
        </div>

        {/* data table trigger */}
        <button
          className="network-table-btn"
          onClick={() => setShowTableModal(true)}
          title="View Data Table"
        >
          <i className="fas fa-table"></i>
        </button>

        {placeholder ? (
          <div className="network-placeholder">{placeholder}</div>
        ) : (
          <>
            {/* Tooltip element (DOM-managed, no React state churn) */}
            <div ref={tooltipRef} className="network-tooltip" style={{ display: 'none' }} />

            <ForceGraph2D
              ref={fgRef}
              width={dimensions.width}
              height={dimensions.height}
              // IMPORTANT: pass fresh clones so ForceGraph can mutate safely
              graphData={filtered}
              nodeLabel={nodeLabel}
              nodeCanvasObject={drawNode}
              linkCanvasObject={drawLink}
              linkHoverPrecision={8}
              onLinkHover={(l) => (l ? showTooltipForLink(l) : hideTooltip())}
              cooldownTicks={120}
              d3AlphaDecay={0.03}
              d3VelocityDecay={0.3}
              enableZoomInteraction
              enablePanInteraction
              enableNodeDrag
            />
          </>
        )}
      </div>

      {/* Table Modal */}
      {showTableModal && (
        <NetworkTableModal
          rows={parsed.tableRows}
          tableDefaultVisible={tableDefaultVisible}
          tableOverridesMap={tableOverridesMap}
          setTableDefaultVisible={setTableDefaultVisible}
          setTableOverridesMap={setTableOverridesMap}
          onClose={() => setShowTableModal(false)}
        />
      )}
    </div>
  );
};

export default MultiVariantNetwork;
